import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  ListItemText,
  Alert,
  Typography,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  Menu,
} from "@mui/material";
import { Search } from '@mui/icons-material';
import { Edit, Delete, Download, ViewColumn, FilterList, DensityMedium, GridOn, GridOff } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme, styled, alpha } from "@mui/material";
import { useEffect, useState, useMemo, useCallback } from "react";
import MarqueService from "../../api/services/MarqueService";
import { CircularProgress } from "@mui/material";
import { Snackbar, Slide } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useGridApiRef } from "@mui/x-data-grid";

const StyledCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha("#2a5fa9", 0.05)} 0%, ${alpha("#65b986", 0.05)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: "16px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 20px 40px ${alpha("#2a5fa9", 0.1)}`,
    border: `1px solid ${alpha("#2a5fa9", 0.2)}`,
  },
  padding: "24px",
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: "none",
  borderRadius: "16px",
  background: `linear-gradient(135deg,
    ${alpha(theme.palette.background.paper, 0.9)} 0%,
    ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  backdropFilter: "blur(10px)",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
  width: "100%",
  height: "100%",
  "& .MuiDataGrid-main": {
    overflow: "visible",
  },
  "& .MuiDataGrid-columnHeaders": {
    background: `linear-gradient(90deg,
      #2a5fa9 0%, #3a7fb5 20%, #4a9fc0 40%,
      #5ab8a0 60%, #70bd8d 80%, #8fc68b 100%)`,
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.875rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "none",
    minHeight: "56px",
    overflow: "visible",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
    padding: "16px",
    "&:nth-of-type(even)": {
      backgroundColor: alpha(theme.palette.action.hover, 0.02),
    },
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "none",
    background: `linear-gradient(135deg, #fcfdff 0%, #f6f9fa 100%)`,
    color: theme.palette.text.primary,
  },
  "& .MuiCheckbox-root": {
    color: `${theme.palette.primary.main} !important`,
  },
  "& .MuiDataGrid-virtualScroller": {
    overflowX: "auto",
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: alpha(theme.palette.divider, 0.1),
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: alpha("#2a5fa9", 0.3),
      borderRadius: "4px",
      "&:hover": {
        background: alpha("#2a5fa9", 0.5),
      },
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
      padding: "8px",
      fontSize: "0.75rem",
    },
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, #2a5fa9 0%, #65b986 100%)`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: 700,
  fontSize: "1.5rem",
  marginBottom: "24px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  background: alpha("#2a5fa9", 0.1),
  color: "#2a5fa9",
  "&:hover": {
    background: alpha("#2a5fa9", 0.2),
    transform: "scale(1.1)",
  },
  "&:disabled": {
    background: alpha(theme.palette.action.disabled, 0.1),
    color: theme.palette.action.disabled,
  },
  transition: "all 0.2s ease",
}));

const getChipStyles = (theme) => ({
  backgroundColor: alpha("#4cbed8", 0.1),
  color: "#2a5fa9",
  border: `1px solid ${alpha("#4cbed8", 0.3)}`,
  fontWeight: 600,
  fontSize: "0.75rem",
  borderRadius: "8px",
  margin: "2px",
  "&:hover": {
    backgroundColor: alpha("#4cbed8", 0.2),
  },
});

const protectionZones = [
  { id: 1, name: "France" },
  { id: 2, name: "UE" },
  { id: 3, name: "UK" },
  { id: 4, name: "USA" },
  { id: 5, name: "Canada" },
  { id: 6, name: "Suisse" },
];

const etatsDisponibles = [
  'Actif',
  'Non actif',
  'En cours',
  'Renouvelé',
  'Non renouvelé',
  'Problème',
  'Protégé',
  'Enregistré',
  'Opposition',
  'Opposable',
  'Déposé',
  'A déposer',
  'Cédée',
  'Non déposée',
  'Refus',
  'Expiré',
  'Enregistrée',
  'Protégée',
  'Renouvelée'
];

const CustomToolbar = ({ setAnchorElColumns, setAnchorElDensity }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      p: 1,
      display: "flex",
      alignItems: "center",
      background: alpha(theme.palette.background.paper, 0.8),
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    }}>
      <Box display="flex" gap={1}>
        <Tooltip title="Colonnes">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              onClick={(e) => setAnchorElColumns(e.currentTarget)}
              size="small"
            >
              <ViewColumn fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#000000', userSelect: 'none' }}>
              Colonnes
            </Typography>
          </Box>
        </Tooltip>

        <Tooltip title="Densité">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              onClick={(e) => setAnchorElDensity(e.currentTarget)}
              size="small"
            >
              <DensityMedium fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#000000', userSelect: 'none' }}>
              Densité
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

const Marques = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const userRole = localStorage.getItem("user_role");
  const token = localStorage.getItem("token");
  const [marques, setMarques] = useState([]);
  const [classes, setClasses] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [selectedMarque, setSelectedMarque] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [firstErrorField, setFirstErrorField] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitAttemptedEdit, setSubmitAttemptedEdit] = useState(false);
  const [customProprietaire, setCustomProprietaire] = useState(false);
  const [customMandataire, setCustomMandataire] = useState(false);
  const [customProprietaireEdit, setCustomProprietaireEdit] = useState(false);
  const [customMandataireEdit, setCustomMandataireEdit] = useState(false);
  const [customCompteComptableAdd, setCustomCompteComptableAdd] = useState(false);
  const [customCompteComptableEdit, setCustomCompteComptableEdit] = useState(false);
  const [anchorElColumns, setAnchorElColumns] = useState(null);
  const [anchorElDensity, setAnchorElDensity] = useState(null);
  const [columnSearch, setColumnSearch] = useState("");
  const [sortByDateDesc, setSortByDateDesc] = useState(false);
  const apiRef = useGridApiRef();
  const [filterFromAlerte, setFilterFromAlerte] = useState(false);
  const [alerteFilterNom, setAlerteFilterNom] = useState("");

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    try {
      const saved = localStorage.getItem("marques_column_visibility");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });


  const [density, setDensity] = useState(() => {
    return localStorage.getItem("marques_density") || 'standard';
  });
  const [newMarque, setNewMarque] = useState({
    numero: "", nom: "", proprietaire: "", descriptif: "", numero_marque: "", mandataire: "",
    surveillance_externe: false, cabinet_surveilleur: "", numero_dossier_surveilleur: "",
    classes: [],
    zone_de_protection: [],
    date_depot: "", date_de_publication: "",
    date_de_fin_opposition: "", date_de_protection: "", date_de_renouvellement: "",
    etat_de_la_marque: "", noms_domaine_associes: "", certificat_depot_pdf: null,
    certificat_enregistrement_pdf: null, commentaire: "", compte_comptable_id: "",
    compte_comptable_manuel: "", societe_id: "",
  });
  const [comptesComptables, setComptesComptables] = useState([]);
  const [loadingComptes, setLoadingComptes] = useState(false);
  const [comptesComptablesEdit, setComptesComptablesEdit] = useState([]);
  const [loadingComptesEdit, setLoadingComptesEdit] = useState(false);
  const [filterNom, setFilterNom] = useState("");
  const [filterDateRenouvellement, setFilterDateRenouvellement] = useState("");
  const [error, setError] = useState("");
  const [filterSociete, setFilterSociete] = useState("");
  const [societes, setSocietes] = useState([]);

  const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
  const [isClassesSelectOpen, setIsClassesSelectOpen] = useState(false);
  const [isAddClassesSelectOpen, setIsAddClassesSelectOpen] = useState(false);

  const location = useLocation();

  // Highlight de la ligne venant des alertes
  const [highlightId, setHighlightId] = useState(null);


  const nextNumero = useMemo(() => {
    const numeros = marques
      .map(m => m.numero)
      .filter(n => typeof n === "string" && /^M\d+$/i.test(n.trim()))
      .map(n => parseInt(n.trim().substring(1), 10))
      .filter(n => !isNaN(n));

    const max = numeros.length ? Math.max(...numeros) : 0;
    return `M${max + 1}`;
  }, [marques]);

  const uniqueProprietaires = useMemo(() => {
    const proprietaires = marques.map(m => m.proprietaire).filter(p => p && p.trim() !== "").map(p => p.trim());
    return [...new Set(proprietaires)].sort();
  }, [marques]);

  const uniqueCabinetsSurveilleurs = useMemo(() => {
    const cabinets = marques.map(m => m.cabinet_surveilleur).filter(c => c && c.trim() !== "").map(c => c.trim());
    return [...new Set(cabinets)].sort();
  }, [marques]);

  const uniqueMandataires = useMemo(() => {
    const mandataires = marques.map(m => m.mandataire).filter(m => m && m.trim() !== "").map(m => m.trim());
    return [...new Set(mandataires)].sort();
  }, [marques]);

  // Sociétés uniques à partir des marques
  const uniqueSocietes = useMemo(() => {
    const map = new Map();
    marques.forEach(m => {
      if (m.societe?.id && m.societe?.nom) {
        map.set(m.societe.id, { id: m.societe.id, nom: m.societe.nom });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.nom.localeCompare(b.nom));
  }, [marques]);


  const fetchMarques = useCallback(async () => {
    if (!token) { setError("Utilisateur non authentifié."); return; }
    try {
      const responseData = await MarqueService.getMarques(userRole, token);

      const marquesData = Array.isArray(responseData)
        ? responseData
        : responseData.resultats || [];

      setMarques(marquesData);
      if (responseData.societes && responseData.societes.length > 0) {
        setSocietes(responseData.societes);
      } else {
        const societesMap = new Map();
        marquesData.forEach(m => {
          if (m.societe?.id && m.societe?.nom) {
            societesMap.set(m.societe.id, { id: m.societe.id, nom: m.societe.nom });
          }
        });
        setSocietes(Array.from(societesMap.values()).sort((a, b) => a.nom.localeCompare(b.nom)));
      }

      setError("");
    } catch (err) {
      setError(err.message || "Erreur lors de la récupération des marques.");
    }
  }, [token, userRole]);

  const toolbarComponent = useCallback(() => (
    <CustomToolbar
      setAnchorElColumns={setAnchorElColumns}
      setAnchorElDensity={setAnchorElDensity}
    />
  ), []);

  const fetchClasses = useCallback(async () => {
    if (!token) { setError("Utilisateur non authentifié."); return; }
    try {
      const classesData = await MarqueService.getClasses(userRole, token);
      const classesArray = Array.isArray(classesData) ? classesData : classesData.resultats || [];
      if (classesArray.length === 0) { setError("Aucune classe disponible."); }
      setClasses(classesArray);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la récupération des classes.");
    }
  }, [token, userRole]);


  const chargerComptesComptables = async (societeId, isEdit = false) => {
    if (!societeId) {
      isEdit ? setComptesComptablesEdit([]) : setComptesComptables([]);
      return;
    }
    isEdit ? setLoadingComptesEdit(true) : setLoadingComptes(true);
    try {
      const data = await MarqueService.getComptesComptables(userRole, token, societeId);
      isEdit ? setComptesComptablesEdit(data) : setComptesComptables(data);
    } catch (err) {
      setSnackbarMessage("Impossible de charger le plan comptable pour cette société.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      isEdit ? setComptesComptablesEdit([]) : setComptesComptables([]);
    } finally {
      isEdit ? setLoadingComptesEdit(false) : setLoadingComptes(false);
    }
  };

  useEffect(() => {
    localStorage.setItem("marques_column_visibility", JSON.stringify(columnVisibilityModel));
  }, [columnVisibilityModel]);

  useEffect(() => {
    localStorage.setItem("marques_density", density);
  }, [density])

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchMarques(), fetchClasses()]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchMarques, fetchClasses]);

  useEffect(() => {
    if (snackbarOpen && firstErrorField) {
      setTimeout(() => {
        const selectors = {
          compte_comptable_id: '#compte-comptable-label',
          classes: '#classes-label',
          zone_de_protection: '#zone-protection-label',
        };
        const selector = selectors[firstErrorField];
        const element = document.querySelector(selector);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          const input = element.closest('div')?.querySelector('input, div[role="button"]');
          input?.focus?.();
        }
      }, 100);
    }
  }, [snackbarOpen, firstErrorField]);


  useEffect(() => {
    if (snackbarOpen && firstErrorField) {
      setTimeout(() => {
        const selectors = {
          numero: '[name="Numéro"]',
          compte_comptable_id: openEdit ? '#compte-comptable-edit-label' : '#compte-comptable-label',
          classes: openEdit ? '#classes-edit-label' : '#classes-label',
          zone_de_protection: openEdit ? '#zone-protection-edit-label' : '#zone-protection-label',
        };
        const selector = selectors[firstErrorField];
        const element = document.querySelector(selector);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          const input = element.closest('div')?.querySelector('input, div[role="button"]');
          input?.focus?.();
        }
      }, 100);
    }
  }, [snackbarOpen, firstErrorField, openEdit]);



  const filteredMarques = useMemo(() => {
    let result = marques.filter(m => {
      const matchNom = filterNom
        ? m.nom?.toLowerCase().includes(filterNom.toLowerCase())
        : true;

      const matchDate = filterDateRenouvellement
        ? m.date_de_renouvellement &&
        new Date(m.date_de_renouvellement).toISOString().split("T")[0] === filterDateRenouvellement
        : true;

      const matchSociete = filterSociete
        ? String(m.societe?.id) === String(filterSociete)
        : true;

      // Filtre prioritaire venant d'une alerte
      const matchAlerte = filterFromAlerte && alerteFilterNom
        ? m.nom?.toLowerCase().trim() === alerteFilterNom.toLowerCase().trim()
        : true;

      return matchNom && matchDate && matchSociete && matchAlerte;
    });

    if (sortByDateDesc) {
      result = [...result].sort((a, b) => {
        const dateA = new Date(a.date_de_renouvellement || a.date_de_protection || a.date_depot || 0);
        const dateB = new Date(b.date_de_renouvellement || b.date_de_protection || b.date_depot || 0);
        return dateB - dateA;
      });
    }

    return result;
  }, [marques, filterNom, filterDateRenouvellement, filterSociete, sortByDateDesc, filterFromAlerte, alerteFilterNom]);

  const [paginationModel, setPaginationModel] = useState({ pageSize: 20, page: 0 });

  useEffect(() => {
    if (!location.state?.highlightId || marques.length === 0) return;

    const id = location.state.highlightId;
    const nomMarque = location.state.highlightItem; // ← on récupère aussi le nom

    // Réinitialiser les autres filtres
    setFilterNom("");
    setFilterDateRenouvellement("");
    setFilterSociete("");

    if (nomMarque) {
      // Mode "filtre depuis alerte"
      setAlerteFilterNom(nomMarque);
      setFilterFromAlerte(true);
      setHighlightId(id);
    } else {
      // Fallback ancien comportement (highlight seulement)
      setHighlightId(id);
      setFilterFromAlerte(false);
    }
  }, [location.state, marques]);

  // Highlight + Scroll vers la ligne venant des alertes
  useEffect(() => {
    if (!highlightId || filteredMarques.length === 0) return;

    const rowIndex = filteredMarques.findIndex(m => m.marque_id === highlightId);
    if (rowIndex === -1) {
      setHighlightId(null);
      return;
    }

    const pageSize = paginationModel.pageSize;
    const targetPage = Math.floor(rowIndex / pageSize);
    const rowIndexInPage = rowIndex % pageSize;

    // 1. Changer de page si nécessaire
    if (paginationModel.page !== targetPage) {
      setPaginationModel(prev => ({ ...prev, page: targetPage }));
    }

    // 2. Attendre que la grille soit mise à jour (page changée + render)
    const timeoutId = setTimeout(() => {
      if (!apiRef.current) return;

      try {
        // Méthode la plus fiable
        apiRef.current.scrollToIndexes({
          rowIndex: rowIndexInPage,
          colIndex: 0, // optionnel
        });

        // Option de secours : focus sur la ligne
        const rowElement = document.querySelector(`[data-id="${highlightId}"]`);
        if (rowElement) {
          rowElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }

      } catch (err) {
        console.warn("Scroll via API failed, using DOM fallback", err);
        const rowElement = document.querySelector(`[data-id="${highlightId}"]`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }, 450); // 400-500ms est généralement le bon timing

    // Nettoyage du highlight après un certain temps
    const clearId = setTimeout(() => {
      setHighlightId(null);
    }, 8000);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(clearId);
    };
  }, [highlightId, filteredMarques, paginationModel.pageSize, apiRef]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);

    // Si on vient de changer de page à cause d'un highlight, on relance le scroll
    if (highlightId) {
      setTimeout(() => {
        const rowIndex = filteredMarques.findIndex(m => m.marque_id === highlightId);
        if (rowIndex !== -1) {
          const rowIndexInPage = rowIndex % newModel.pageSize;
          apiRef.current?.scrollToIndexes({ rowIndex: rowIndexInPage });
        }
      }, 100);
    }
  };

  const exportMarques = useMemo(() => {
    let data = filteredMarques;
    const userSocieteId = userData?.societe_id || localStorage.getItem("user_societe_id");
    if (userSocieteId) {
      data = data.filter(m => m.societe_id === parseInt(userSocieteId));

    }

    return data;
  }, [filteredMarques, userRole, userData]);

  const handleDownloadExcel = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const timestamp = `${pad(now.getDate())}_${pad(now.getMonth() + 1)}_${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
    const fileName = `marques_${timestamp}.csv`;

    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");
    const fmtCompte = (cc) => {
      if (!cc) return "";
      if (typeof cc === "string") return cc;
      return `${cc.code} - ${cc.libelle}`;
    };
    const fmtZone = (z) =>
      Array.isArray(z)
        ? z.join(" ; ")
        : String(z || "").replace(/[\[\]"]/g, "").trim();
    const fmtClasses = (ids) => {
      if (!Array.isArray(ids) || ids.length === 0) return "Aucune classe";
      return Array.from(new Set(ids))
        .map((id) => {
          const classe = classes.find((c) => c.id === id);
          return classe ? `${classe.numero}` : `Classe ${id} introuvable`;
        })
        .join(" ; ");
    };

    const headers = [
      "Société",
      "Numéro",
      "Compte Comptable",
      "Nom",
      "Descriptif",
      "Numéro Marque",
      "Propriétaire",
      "Mandataire",
      "Surveillance Externe",
      "Cabinet Surveilleur",
      "Numéro Dossier Surveilleur",
      "Classes",
      "Zone de Protection",
      "État de la Marque",
      "Date Dépôt",
      "Date Publication",
      "Date Fin Opposition",
      "Date Protection",
      "Date Renouvellement",
      "Noms Domaine Associés",
      "Commentaire",
    ];

    const data = exportMarques.map((row) => [
      row.societe?.nom || row.societe_nom || "",
      row.numero || "",
      fmtCompte(row.compte_comptable),
      row.nom || "",
      row.descriptif || "",
      row.numero_marque || "",
      row.proprietaire || "",
      row.mandataire || "",
      row.surveillance_externe ? "Oui" : "Non",
      row.cabinet_surveilleur || "",
      row.numero_dossier_surveilleur || "",
      fmtClasses(row.classes),
      fmtZone(row.zone_de_protection),
      row.etat_de_la_marque || "",
      fmtDate(row.date_depot),
      fmtDate(row.date_de_publication),
      fmtDate(row.date_de_fin_opposition),
      fmtDate(row.date_de_protection),
      fmtDate(row.date_de_renouvellement),
      row.noms_domaine_associes || "",
      row.commentaire || "",
    ]);

    const csvContent =
      "\uFEFF" +
      [
        headers.map((h) => `"${h}"`).join(";"),
        ...data.map((row) =>
          row
            .map((cell) => `"${String(cell).replace(/"/g, '""').replace(/\n|\r/g, " ")}"`)
            .join(";")
        ),
      ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const calculateDerivedDates = (dateDepot, datePublication) => {
    let date_de_protection = "", date_de_renouvellement = "", date_de_fin_opposition = "";
    if (dateDepot && !isNaN(new Date(dateDepot))) {
      const depot = new Date(dateDepot);
      date_de_protection = depot.toISOString().split("T")[0];
      const renouvellement = new Date(depot);
      renouvellement.setFullYear(depot.getFullYear() + 10);
      date_de_renouvellement = renouvellement.toISOString().split("T")[0];
    }
    if (datePublication && !isNaN(new Date(datePublication))) {
      const publication = new Date(datePublication);
      const opposition = new Date(publication);
      opposition.setMonth(publication.getMonth() + 2);
      date_de_fin_opposition = opposition.toISOString().split("T")[0];
    }
    return { date_de_protection, date_de_renouvellement, date_de_fin_opposition };
  };

  const handleEditClick = (marque) => {
    const { date_de_protection, date_de_renouvellement, date_de_fin_opposition } = calculateDerivedDates(
      marque.date_depot,
      marque.date_de_publication
    );

    setSelectedMarque({
      ...marque,
      numero: marque.numero || "",
      nom: marque.nom || "",
      proprietaire: marque.proprietaire || "",
      descriptif: marque.descriptif || "",
      numero_marque: marque.numero_marque || "",
      mandataire: marque.mandataire || "",
      surveillance_externe: Boolean(marque.surveillance_externe),
      cabinet_surveilleur: marque.cabinet_surveilleur || "",
      numero_dossier_surveilleur: marque.numero_dossier_surveilleur || "",
      classes: Array.isArray(marque.classes) ? Array.from(new Set(marque.classes)) : [],
      zone_de_protection: Array.isArray(marque.zone_de_protection)
        ? Array.from(new Set(marque.zone_de_protection))
        : [],
      date_depot: marque.date_depot ? new Date(marque.date_depot).toISOString().split("T")[0] : "",
      date_de_publication: marque.date_de_publication
        ? new Date(marque.date_de_publication).toISOString().split("T")[0]
        : "",
      date_de_fin_opposition,
      date_de_protection,
      date_de_renouvellement,
      etat_de_la_marque: marque.etat_de_la_marque || "",
      noms_domaine_associes: marque.noms_domaine_associes || "",
      commentaire: marque.commentaire || "",
      compte_comptable_id: marque.compte_comptable_id || "",
      compte_comptable_manuel: marque.compte_comptable_id
        ? ""
        : (typeof marque.compte_comptable === "string" ? marque.compte_comptable : ""),
      certificat_depot_pdf: null,
      certificat_enregistrement_pdf: null,
      existing_certificat_depot_pdf: marque.certificat_depot_pdf || null,
      existing_certificat_enregistrement_pdf: marque.certificat_enregistrement_pdf || null,
      societe_id: marque.societe_id || "",
    });

    // Active le bon mode d'affichage : manuel si pas d'ID mais un texte, liste sinon
    setCustomCompteComptableEdit(
      !marque.compte_comptable_id && typeof marque.compte_comptable === "string" && marque.compte_comptable.trim() !== ""
    );

    chargerComptesComptables(marque.societe_id, true);
    setSubmitAttemptedEdit(false); // Réinitialise la validation
    setOpenEdit(true);
  };

  const handleDeleteClick = (marque) => {
    setSelectedMarque(marque);
    setOpenDelete(true);
  };

  const handleEditSubmit = async () => {
    setSubmitAttemptedEdit(true);
    setError("");
    setSnackbarMessage("");
    setFirstErrorField("");

    const validClasses = (selectedMarque?.classes || []).filter(
      id => id !== undefined && id !== null && id !== '' && Number.isInteger(id)
    );

    if (!selectedMarque?.numero?.trim()) {
      setSnackbarMessage("Le champ numéro est requis.");
      setSnackbarSeverity("error");
      setFirstErrorField("numero");
      setSnackbarOpen(true);
      return;
    }

    if (!selectedMarque?.compte_comptable_id && !selectedMarque?.compte_comptable_manuel?.trim()) {
      setSnackbarMessage("Le champ Compte Comptable est requis.");
      setSnackbarSeverity("error");
      setFirstErrorField("compte_comptable_id");
      setSnackbarOpen(true);
      return;
    }

    if (!validClasses.length) {
      setSnackbarMessage("Veuillez sélectionner au moins une classe.");
      setSnackbarSeverity("error");
      setFirstErrorField("classes");
      setSnackbarOpen(true);
      return;
    }

    if (!selectedMarque?.zone_de_protection?.length) {
      setSnackbarMessage("Veuillez sélectionner au moins une zone de protection.");
      setSnackbarSeverity("error");
      setFirstErrorField("zone_de_protection");
      setSnackbarOpen(true);
      return;
    }

    if (!selectedMarque?.societe_id) {
      setSnackbarMessage("Veuillez sélectionner une société.");
      setSnackbarSeverity("error");
      setFirstErrorField("societe_id");
      setSnackbarOpen(true);
      return;
    }

    try {
      const { date_de_protection, date_de_renouvellement, date_de_fin_opposition } = calculateDerivedDates(
        selectedMarque.date_depot,
        selectedMarque.date_de_publication
      );
      const marqueToUpdate = {
        ...selectedMarque,
        classes: validClasses,
        date_de_protection: date_de_protection || selectedMarque.date_de_protection,
        date_de_renouvellement: date_de_renouvellement || selectedMarque.date_de_renouvellement,
        date_de_fin_opposition: date_de_fin_opposition || selectedMarque.date_de_fin_opposition,
        societe_id: selectedMarque.societe_id,
      };
      await MarqueService.updateMarque(userRole, token, selectedMarque.marque_id, marqueToUpdate);
      setOpenEdit(false);
      setSubmitAttemptedEdit(false);
      setSnackbarMessage("Marque modifiée avec succès !");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      await fetchMarques();
    } catch (err) {
      const errorMessage = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(", ")
        : err.response?.data?.message || "Erreur lors de la modification de la marque.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await MarqueService.deleteMarque(userRole, token, selectedMarque.marque_id);
      setOpenDelete(false);
      setSnackbarMessage("Marque supprimée avec succès !");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      await fetchMarques();
    } catch (err) {
      setSnackbarMessage("Erreur lors de la suppression de la marque.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleAddSubmit = async () => {
    setSubmitAttempted(true);
    setError("");
    setSnackbarMessage("");
    setFirstErrorField("");

    const validClasses = (newMarque.classes || []).filter(
      id => id !== undefined && id !== null && id !== '' && Number.isInteger(id)
    );

    if (!newMarque.numero.trim()) {
      setSnackbarMessage("Le champ numéro est requis.");
      setSnackbarSeverity("error");
      setFirstErrorField("numero");
      setSnackbarOpen(true);
      return;
    }
    if (!newMarque.compte_comptable_id && !newMarque.compte_comptable_manuel?.trim()) {
      setSnackbarMessage("Le champ Compte Comptable est requis.");
      setSnackbarSeverity("error");
      setFirstErrorField("compte_comptable_id");
      setSnackbarOpen(true);
      return;
    }
    if (!validClasses.length) {
      setSnackbarMessage("Veuillez sélectionner au moins une classe.");
      setSnackbarSeverity("error");
      setFirstErrorField("classes");
      setSnackbarOpen(true);
      return;
    }
    if (!newMarque.zone_de_protection.length) {
      setSnackbarMessage("Veuillez sélectionner au moins une zone de protection.");
      setSnackbarSeverity("error");
      setFirstErrorField("zone_de_protection");
      setSnackbarOpen(true);
      return;
    }

    if (!newMarque.societe_id) {
      setSnackbarMessage("Veuillez sélectionner une société.");
      setSnackbarSeverity("error");
      setFirstErrorField("societe_id");
      setSnackbarOpen(true);
      return;
    }

    try {
      const { date_de_protection, date_de_renouvellement, date_de_fin_opposition } = calculateDerivedDates(
        newMarque.date_depot,
        newMarque.date_de_publication
      );
      const marqueToAdd = {
        ...newMarque,
        classes: validClasses,
        date_de_protection,
        date_de_renouvellement,
        date_de_fin_opposition,
        societe_id: newMarque.societe_id,
      };
      await MarqueService.addMarque(userRole, token, marqueToAdd);
      setOpenAdd(false);
      setSubmitAttempted(false);
      setNewMarque({
        numero: "", nom: "", proprietaire: "", descriptif: "", numero_marque: "", mandataire: "",
        surveillance_externe: false, cabinet_surveilleur: "", numero_dossier_surveilleur: "",
        classes: [],
        zone_de_protection: [],
        date_depot: "", date_de_publication: "",
        date_de_fin_opposition: "", date_de_protection: "", date_de_renouvellement: "",
        etat_de_la_marque: "", noms_domaine_associes: "", certificat_depot_pdf: null,
        certificat_enregistrement_pdf: null, commentaire: "", compte_comptable_id: "",
        compte_comptable_manuel: "", societe_id: "",
      });
      setComptesComptables([]);
      setCustomCompteComptableAdd(false);
      setSnackbarMessage("Marque ajoutée avec succès !");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      await fetchMarques();
    } catch (err) {
      const errorMessage = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(", ")
        : err.response?.data?.message || "Erreur lors de l'ajout de la marque.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };



  const columns = [
    {
      field: "compte_comptable",
      headerName: "Compte Comptable",
      flex: 2,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const cc = params.row.compte_comptable;
        if (!cc) return "-";
        if (typeof cc === "string") return cc; // saisie manuelle
        return `${cc.code} - ${cc.libelle}`; // choisi dans la liste
      },
    },

    {
      field: "numero",
      headerName: "Numéro",
      flex: 0.7,
      minWidth: 80,
      sortable: false,
    },
    {
      field: "nom",
      headerName: "Nom",
      flex: 1,
      minWidth: 100,
      sortable: false,
    },
    {
      field: "classes",
      headerName: "Classes",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Box>
          {params.row.classes?.length
            ? Array.from(new Set(params.row.classes))
              .map((id) => {
                const classe = classes.find((c) => c.id === id);
                return classe ? `${classe.numero} ` : `Classe ${id} introuvable`;
              })
              .filter(Boolean)
              .map((label, index) => (
                <Chip
                  key={index}
                  label={label}
                  size="small"
                  sx={{
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    border: "none",
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                />
              ))
            : <Typography variant="body2" color="textSecondary">-</Typography>}
        </Box>
      ),
      sortable: false,
    },
    {
      field: "zone_de_protection",
      headerName: "Zone de Protection",
      flex: 1,
      minWidth: 180,
      sortable: false,
    },
    {
      field: "etat_de_la_marque",
      headerName: "État",
      flex: 3,
      minWidth: 150,
      renderCell: (params) => (
        <Chip
          label={params.value || "-"}
          size="small"
          sx={getChipStyles(theme)}
        />
      ),
      sortable: false,
    },
    {
      field: "date_depot",
      headerName: "Date Dépôt",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "-"}
        </Typography>
      ),
      sortable: true,
    },
    {
      field: "date_de_publication",
      headerName: "Date Publication",
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "-"}
        </Typography>
      ),
      sortable: true,
    },
    {
      field: "date_de_fin_opposition",
      headerName: "Date Fin Opposition",
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "-"}
        </Typography>
      ),
      sortable: true,
    },
    {
      field: "date_de_protection",
      headerName: "Date Protection",
      flex: 1.1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "-"}
        </Typography>
      ),
      sortable: true,
    },
    {
      field: "date_de_renouvellement",
      headerName: "Date Renouvellement",
      flex: 2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "-"}
        </Typography>
      ),
      sortable: true,
    },
    {
      field: "societe_nom",
      headerName: "Société",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.societe?.nom || "-"}
        </Typography>
      ),
      sortable: false,
    },
    {
      field: "certificat_depot_pdf",
      headerName: "Cert. Dépôt PDF",
      flex: 1,
      minWidth: 130,
      renderCell: (params) =>
        params.row.certificat_depot_pdf ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={() =>
              window.open(
                `${process.env.REACT_APP_API_URL}/storage/certificats/${params.row.certificat_depot_pdf.split("/").pop()}`,
                "_blank"
              )
            }
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.3),
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            Télécharger
          </Button>
        ) : (
          <Typography variant="body2" color="textSecondary">-</Typography>
        ),
      sortable: false,
    },
    {
      field: "certificat_enregistrement_pdf",
      headerName: "Cert. Enreg. PDF",
      flex: 2,
      minWidth: 150,
      renderCell: (params) =>
        params.row.certificat_enregistrement_pdf ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={() =>
              window.open(
                `${process.env.REACT_APP_API_URL}/storage/certificats/${params.row.certificat_enregistrement_pdf.split("/").pop()}`,
                "_blank"
              )
            }
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.3),
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            Télécharger
          </Button>
        ) : (
          <Typography variant="body2" color="textSecondary">-</Typography>
        ),
      sortable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.9,
      minWidth: 100,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Modifier">
            <StyledIconButton
              onClick={() => handleEditClick(params.row)}
              size="small"
            >
              <Edit fontSize="small" />
            </StyledIconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <StyledIconButton
              onClick={() => handleDeleteClick(params.row)}
              size="small"
              sx={{ color: theme.palette.error.main }}
            >
              <Delete fontSize="small" />
            </StyledIconButton>
          </Tooltip>
        </Box>
      ),
      sortable: false,
    },
  ];



  return (
    <Box sx={{ m: { xs: 1, sm: 2, md: 3 } }}>
      <Header title="MARQUES" subtitle="Gestion centralisée des marques enregistrées" />
      <StyledCard>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {/* Filtres manuels */}
        <Box mb={2} display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={2}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            width={{ xs: "100%", sm: "auto" }}
            sx={{
              minWidth: { sm: 400, md: 500 },
              maxWidth: { md: 800 },
            }}
          >
            <TextField label="Rechercher par nom" variant="outlined" size="small" value={filterNom} onChange={(e) => setFilterNom(e.target.value)} sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "8px", width: { xs: "100%", sm: "100%" } }} />
            <TextField label="Filtrer par date de renouvellement" type="date" variant="outlined" size="small" value={filterDateRenouvellement} onChange={(e) => setFilterDateRenouvellement(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "8px", width: { xs: "100%", sm: "100%" } }} />

            <FormControl size="small" sx={{ minWidth: 180, backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "8px" }}>
              <InputLabel>Toutes les sociétés</InputLabel>
              <Select
                value={filterSociete}

                onChange={(e) => setFilterSociete(e.target.value)}
                label="Toutes les sociétés"
                sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "8px" }}

              >
                <MenuItem value="">
                  Toutes les sociétés
                </MenuItem>
                {societes.map((societe) => (
                  <MenuItem key={societe.id} value={societe.id}>
                    {societe.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>


          </Box>
          <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1} width={{ xs: "100%", sm: "auto" }}>
            {(userRole === "gestionnaire" || userRole === "admin") && (
              <Button
                variant="contained"
                onClick={() => {
                  setNewMarque({
                    numero: nextNumero,
                    nom: "", proprietaire: "", descriptif: "", numero_marque: "", mandataire: "",
                    surveillance_externe: false, cabinet_surveilleur: "", numero_dossier_surveilleur: "",
                    classes: [],
                    zone_de_protection: [],
                    date_depot: "", date_de_publication: "",
                    date_de_fin_opposition: "", date_de_protection: "", date_de_renouvellement: "",
                    etat_de_la_marque: "", noms_domaine_associes: "", certificat_depot_pdf: null,
                    certificat_enregistrement_pdf: null, commentaire: "", compte_comptable_id: "",
                    compte_comptable_manuel: "", societe_id: "",
                  });
                  setCustomCompteComptableAdd(false);
                  setOpenAdd(true);
                }}
                sx={{ borderRadius: "8px", background: `linear-gradient(135deg, #2a5fa9 0%, #4cbed8 100%)`, color: "#ffffff", "&:hover": { background: `linear-gradient(135deg, #4cbed8 0%, #65b986 100%)`, transform: "scale(1.05)" }, width: { xs: "100%", sm: "auto" } }}
              >
                Ajouter une marque
              </Button>
            )}
            <Button variant="contained" color="primary" startIcon={<Download />} onClick={handleDownloadExcel} sx={{ borderRadius: "8px", background: `linear-gradient(135deg, #2a5fa9 0%, #4cbed8 100%)`, color: "#ffffff", "&:hover": { background: `linear-gradient(135deg, #4cbed8 0%, #65b986 100%)`, transform: "scale(1.05)" }, width: { xs: "100%", sm: "auto" } }}>
              Télécharger Excel
            </Button>
          </Box>
        </Box>

        {/* MENU COLONNES  */}
        <Menu
          anchorEl={anchorElColumns}
          open={Boolean(anchorElColumns)}
          onClose={() => setAnchorElColumns(null)}
          disableAutoFocusItem
          keepMounted
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: { maxHeight: 500, width: 320, '& .MuiList-root': { p: 0 } },
          }}
        >
          <Box sx={{ px: 1, pt: 1 }}>
            <TextField
              fullWidth
              placeholder="Rechercher une colonne"
              size="small"
              variant="outlined"
              value={columnSearch}
              onChange={(e) => setColumnSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: <Search fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
              }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
            />
          </Box>

          <Box sx={{ maxHeight: 320, overflowY: 'auto', px: 1, py: 1 }}>
            {[
              { field: "compte_comptable", headerName: "Compte Comptable" },
              { field: "numero", headerName: "Numéro" },
              { field: "nom", headerName: "Nom" },
              { field: "classes", headerName: "Classes" },
              { field: "zone_de_protection", headerName: "Zone de Protection" },
              { field: "etat_de_la_marque", headerName: "État" },
              { field: "date_depot", headerName: "Date Dépôt" },
              { field: "date_de_publication", headerName: "Date Publication" },
              { field: "date_de_fin_opposition", headerName: "Date Fin Opposition" },
              { field: "date_de_protection", headerName: "Date Protection" },
              { field: "date_de_renouvellement", headerName: "Date Renouvellement" },
              { field: "certificat_depot_pdf", headerName: "Cert. Dépôt PDF" },
              { field: "certificat_enregistrement_pdf", headerName: "Cert. Enreg. PDF" },
              { field: "societe_nom", headerName: "Société" },
              { field: "actions", headerName: "Actions" },
            ]
              .filter(col => col.headerName.toLowerCase().includes(columnSearch.toLowerCase()))
              .map((col) => (
                <Box
                  key={col.field}
                  onClick={() => {
                    setColumnVisibilityModel((prev) => ({
                      ...prev,
                      [col.field]: !(prev[col.field] ?? true),
                    }));
                  }}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    mb: 0.25,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <Box sx={{
                    width: 34,
                    height: 20,
                    borderRadius: '10px',
                    backgroundColor: (columnVisibilityModel[col.field] ?? true) ? '#1976d2' : '#e0e0e0',
                    position: 'relative',
                    mr: 1.5,
                    flexShrink: 0,
                  }}>
                    <Box sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      position: 'absolute',
                      top: '2px',
                      left: (columnVisibilityModel[col.field] ?? true) ? '16px' : '2px',
                      transition: 'left 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.875rem', userSelect: 'none' }}>
                    {col.headerName}
                  </Typography>
                </Box>
              ))}
          </Box>

          <Divider sx={{ my: 0.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5 }}>
            <Button
              size="small"
              onClick={() => {
                const allHidden = {
                  compte_comptable: false,
                  numero: false,
                  nom: false,
                  classes: false,
                  zone_de_protection: false,
                  etat_de_la_marque: false,
                  date_depot: false,
                  date_de_publication: false,
                  date_de_fin_opposition: false,
                  date_de_protection: false,
                  date_de_renouvellement: false,
                  certificat_depot_pdf: false,
                  certificat_enregistrement_pdf: false,
                  societe_nom: false,
                  actions: false,
                };
                setColumnVisibilityModel(allHidden);
              }}
              sx={{ textTransform: 'none', fontSize: '0.813rem' }}
            >
              MASQUER TOUT
            </Button>

            <Button
              size="small"
              onClick={() => {
                const allVisible = {
                  compte_comptable: true,
                  numero: true,
                  nom: true,
                  classes: true,
                  zone_de_protection: true,
                  etat_de_la_marque: true,
                  date_depot: true,
                  date_de_publication: true,
                  date_de_fin_opposition: true,
                  date_de_protection: true,
                  date_de_renouvellement: true,
                  certificat_depot_pdf: true,
                  certificat_enregistrement_pdf: true,
                  societe_nom: true,
                  actions: true,
                };

                setColumnVisibilityModel(allVisible);
              }}
              sx={{ textTransform: 'none', fontSize: '0.813rem' }}
            >
              AFFICHER TOUT
            </Button>
          </Box>
        </Menu>

        {/* Menu Densité */}
        <Menu
          anchorEl={anchorElDensity}
          open={Boolean(anchorElDensity)}
          onClose={() => setAnchorElDensity(null)}
        >
          <MenuItem onClick={() => { setDensity('compact'); setAnchorElDensity(null); }}>Compact</MenuItem>
          <MenuItem onClick={() => { setDensity('standard'); setAnchorElDensity(null); }}>Standard</MenuItem>
          <MenuItem onClick={() => { setDensity('comfortable'); setAnchorElDensity(null); }}>Confortable</MenuItem>
        </Menu>
        {/* === ALERTE : Filtre actif depuis une alerte === */}
        {filterFromAlerte && (
          <Alert
            severity="info"
            icon={<Search />}
            sx={{ mb: 2, borderRadius: "12px" }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setFilterFromAlerte(false);
                  setAlerteFilterNom("");
                  setHighlightId(null);
                }}
              >
                Afficher toutes les marques
              </Button>
            }
          >
            Affichage filtré pour la marque :{" "}
            <strong>{alerteFilterNom}</strong>
          </Alert>
        )}
        <Box sx={{
          height: "73vh",
          width: "100%",
          overflow: "hidden"
        }}>
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", gap: 2 }}>
              <CircularProgress size={60} thickness={4} />
              <Typography variant="body2" color="text.secondary">Chargement des marques...</Typography>
            </Box>
          ) : filteredMarques.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <Typography variant="body1" color="text.secondary">Aucune marque trouvée.</Typography>
            </Box>
          ) : (
            <StyledDataGrid
              apiRef={apiRef}
              onPaginationModelChange={handlePaginationModelChange}
              rows={filteredMarques}
              columns={columns}
              getRowId={(row) => row.marque_id}
              pagination
              paginationMode="client"
              paginationModel={paginationModel}
              pageSizeOptions={[10, 20, 50, 100]}
              loading={loading}
              disableSelectionOnClick
              density={density}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
              disableColumnSorting={true}
              disableColumnMenu={true}
              getRowClassName={(params) =>
                params.id === highlightId ? "highlighted-row" : ""
              }
              components={{
                Toolbar: toolbarComponent
              }}
              localeText={{
                noRowsLabel: "Aucune marque",
                toolbarExportCSV: "Télécharger en CSV",
              }}
              sx={{
                width: "100% !important",
                height: "100% !important",
                "& .MuiDataGrid-columnHeader": {
                  "& .MuiDataGrid-columnSeparator": { visibility: "visible" }
                },
                "& .MuiDataGrid-virtualScroller": {
                  minHeight: "300px",
                },
                "& .MuiDataGrid-sortIcon": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-sortIcon": {
                  display: "none !important",
                },
                "& .highlighted-row": {
                  backgroundColor: `${alpha("#f59e0b", 0.25)} !important`,
                  outline: `2px solid #f59e0b`,
                  outlineOffset: "-2px",
                  transition: "background-color 0.5s ease",
                },
                [theme.breakpoints.down("sm")]: { fontSize: "0.75rem" },
              }}
            />
          )}
        </Box>
      </StyledCard>

      {/* Add Dialog */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(
              theme.palette.background.paper,
              0.95
            )} 100%)`,
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <SectionTitle>Ajouter une Marque</SectionTitle>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Remplissez les informations de la nouvelle marque
          </Typography>
        </DialogTitle>

        <Divider sx={{ mb: 2 }} />

        <DialogContent sx={{ pt: 2 }}>
          {/* Section: Informations Générales */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📋</span> Informations Générales
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Numéro *"
                fullWidth
                value={newMarque.numero}
                onChange={(e) => setNewMarque({ ...newMarque, numero: e.target.value })}
                error={submitAttempted && !newMarque.numero.trim()}
                helperText={submitAttempted && !newMarque.numero.trim() ? "Ce champ est requis" : ""}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />

              <FormControl
                fullWidth
                error={submitAttempted && !newMarque.societe_id}
              >
                <InputLabel id="societe-add-label">Société *</InputLabel>
                <Select
                  labelId="societe-add-label"
                  value={newMarque.societe_id || ""}
                  onChange={(e) => {
                    const societeId = e.target.value;
                    setNewMarque({ ...newMarque, societe_id: societeId, compte_comptable_id: "" });
                    chargerComptesComptables(societeId, false);
                  }}
                  label="Société *"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': { borderRadius: "12px" }
                  }}
                >
                  <MenuItem value="" disabled>
                    Choisir une société
                  </MenuItem>
                  {societes.map((societe) => (
                    <MenuItem key={societe.id} value={societe.id}>
                      {societe.nom}
                    </MenuItem>
                  ))}
                </Select>
                {submitAttempted && !newMarque.societe_id && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                    Veuillez sélectionner une société.
                  </Typography>
                )}
              </FormControl>


              {!customCompteComptableAdd ? (
                <FormControl
                  fullWidth
                  disabled={!newMarque.societe_id || loadingComptes}
                  error={submitAttempted && !newMarque.compte_comptable_id && !newMarque.compte_comptable_manuel}
                >
                  <InputLabel id="compte-comptable-label">Compte Comptable *</InputLabel>
                  <Select
                    labelId="compte-comptable-label"
                    value={newMarque.compte_comptable_id || ""}
                    onChange={(e) => setNewMarque({ ...newMarque, compte_comptable_id: e.target.value })}
                    label="Compte Comptable *"
                    sx={{
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      '& .MuiOutlinedInput-root': { borderRadius: "12px" }
                    }}
                  >
                    <MenuItem value="" disabled>
                      {loadingComptes ? "Chargement..." : "Choisir un compte"}
                    </MenuItem>
                    {comptesComptables.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.code} — {c.libelle}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomCompteComptableAdd(true);
                        setNewMarque({ ...newMarque, compte_comptable_id: "" });
                      }}
                      sx={{
                        mx: 0.5, borderRadius: '8px',
                        color: '#2a5fa9', fontWeight: 600, fontSize: '0.875rem',
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&:hover': { backgroundColor: alpha('#2a5fa9', 0.08) },
                      }}
                    >
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #2a5fa9, #4cbed8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '1.1rem', fontWeight: 700,
                      }}>+</Box>
                      Saisir manuellement
                    </MenuItem>
                  </Select>
                  {!newMarque.societe_id && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Sélectionnez d'abord une société.
                    </Typography>
                  )}
                  {submitAttempted && !newMarque.compte_comptable_id && !newMarque.compte_comptable_manuel && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                      Ce champ est requis.
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <TextField
                  label="Compte Comptable (saisie manuelle) *"
                  fullWidth
                  autoFocus
                  value={newMarque.compte_comptable_manuel || ""}
                  onChange={(e) => setNewMarque({ ...newMarque, compte_comptable_manuel: e.target.value })}
                  error={submitAttempted && !newMarque.compte_comptable_manuel}
                  helperText={submitAttempted && !newMarque.compte_comptable_manuel ? "Ce champ est requis" : ""}
                  sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
                  InputProps={{
                    endAdornment: (
                      <Button size="small" onClick={() => { setCustomCompteComptableAdd(false); setNewMarque({ ...newMarque, compte_comptable_manuel: "" }); }}
                        sx={{ color: "error.main", minWidth: 'auto', fontSize: '0.75rem' }}>
                        ✕
                      </Button>
                    )
                  }}
                />
              )}
              <TextField
                label="Nom"
                fullWidth
                value={newMarque.nom}
                onChange={(e) => setNewMarque({ ...newMarque, nom: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Descriptif"
                fullWidth
                value={newMarque.descriptif}
                onChange={(e) => setNewMarque({ ...newMarque, descriptif: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Numéro Marque"
                fullWidth
                value={newMarque.numero_marque}
                onChange={(e) => setNewMarque({ ...newMarque, numero_marque: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Parties Prenantes */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>👥</span> Parties Prenantes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* PROPRIÉTAIRE */}
              {!customProprietaire ? (
                <FormControl fullWidth>
                  <InputLabel id="proprietaire-label">Propriétaire</InputLabel>
                  <Select
                    labelId="proprietaire-label"
                    value={newMarque.proprietaire || ""}
                    onChange={(e) => setNewMarque({ ...newMarque, proprietaire: e.target.value })}
                    label="Propriétaire"
                    sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "12px" }}
                  >
                    <MenuItem value=""><em>Aucun</em></MenuItem>
                    {uniqueProprietaires.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={(e) => { e.stopPropagation(); setCustomProprietaire(true); setNewMarque({ ...newMarque, proprietaire: "" }); }}
                      sx={{
                        mx: 0.5, borderRadius: '8px',
                        color: '#2a5fa9', fontWeight: 600, fontSize: '0.875rem',
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&:hover': { backgroundColor: alpha('#2a5fa9', 0.08) },
                      }}
                    >
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #2a5fa9, #4cbed8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '1.1rem', fontWeight: 700,
                      }}>+</Box>
                      Saisir manuellement
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Propriétaire"
                  fullWidth
                  autoFocus
                  value={newMarque.proprietaire || ""}
                  onChange={(e) => setNewMarque({ ...newMarque, proprietaire: e.target.value })}
                  sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
                  InputProps={{
                    endAdornment: (
                      <Button size="small" onClick={() => { setCustomProprietaire(false); setNewMarque({ ...newMarque, proprietaire: "" }); }}
                        sx={{ color: "error.main", minWidth: 'auto', fontSize: '0.75rem' }}>
                        ✕
                      </Button>
                    )
                  }}
                />
              )}

              {/* MANDATAIRE */}
              {!customMandataire ? (
                <FormControl fullWidth>
                  <InputLabel id="mandataire-label">Mandataire</InputLabel>
                  <Select
                    labelId="mandataire-label"
                    value={newMarque.mandataire || ""}
                    onChange={(e) => setNewMarque({ ...newMarque, mandataire: e.target.value })}
                    label="Mandataire"
                    sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "12px" }}
                  >
                    <MenuItem value=""><em>Aucun</em></MenuItem>
                    {uniqueMandataires.map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={(e) => { e.stopPropagation(); setCustomMandataire(true); setNewMarque({ ...newMarque, mandataire: "" }); }}
                      sx={{
                        mx: 0.5, borderRadius: '8px',
                        color: '#2a5fa9', fontWeight: 600, fontSize: '0.875rem',
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&:hover': { backgroundColor: alpha('#2a5fa9', 0.08) },
                      }}
                    >
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #2a5fa9, #4cbed8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '1.1rem', fontWeight: 700,
                      }}>+</Box>
                      Saisir manuellement
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Mandataire"
                  fullWidth
                  autoFocus
                  value={newMarque.mandataire || ""}
                  onChange={(e) => setNewMarque({ ...newMarque, mandataire: e.target.value })}
                  sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
                  InputProps={{
                    endAdornment: (
                      <Button size="small" onClick={() => { setCustomMandataire(false); setNewMarque({ ...newMarque, mandataire: "" }); }}
                        sx={{ color: "error.main", minWidth: 'auto', fontSize: '0.75rem' }}>
                        ✕
                      </Button>
                    )
                  }}
                />
              )}

            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Surveillance */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>🔍</span> Surveillance
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newMarque.surveillance_externe}
                    onChange={(e) => setNewMarque({ ...newMarque, surveillance_externe: e.target.checked })}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    Surveillance Externe
                  </Typography>
                }
                sx={{
                  ml: 0,
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              />

              <FormControl fullWidth>
                <InputLabel id="cabinet-surveilleur-label">Cabinet Surveilleur</InputLabel>
                <Select
                  labelId="cabinet-surveilleur-label"
                  value={newMarque.cabinet_surveilleur || ""}
                  onChange={(e) => setNewMarque({ ...newMarque, cabinet_surveilleur: e.target.value })}
                  label="Cabinet Surveilleur"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Aucun</em>
                  </MenuItem>
                  {uniqueCabinetsSurveilleurs.map((cabinet) => (
                    <MenuItem key={cabinet} value={cabinet}>
                      {cabinet}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Numéro Dossier Surveilleur"
                fullWidth
                value={newMarque.numero_dossier_surveilleur}
                onChange={(e) => setNewMarque({ ...newMarque, numero_dossier_surveilleur: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Classification */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>🏷️</span> Classification et Protection
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControl
                fullWidth
                error={submitAttempted && !newMarque.classes.length}
              >
                <InputLabel id="classes-label">Classes *</InputLabel>
                <Select
                  labelId="classes-label"
                  multiple
                  open={isAddClassesSelectOpen}
                  onOpen={() => setIsAddClassesSelectOpen(true)}
                  onClose={() => setIsAddClassesSelectOpen(false)}
                  value={newMarque.classes || []}
                  onChange={(e) => {
                    const validValues = e.target.value.filter(val => val !== undefined && val !== null && val !== '');
                    setNewMarque({ ...newMarque, classes: [...new Set(validValues)] });
                  }}
                  renderValue={(selected) =>
                    selected.length
                      ? selected
                        .map((id) => {
                          const classe = classes.find((c) => c.id === id);
                          return classe ? `${classe.numero} - ${classe.intitule}` : null;
                        })
                        .filter(Boolean)
                        .join(", ")
                      : "Aucune classe sélectionnée"
                  }
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  {classes.length ? (
                    classes.map((classe) => (
                      <MenuItem key={classe.id} value={classe.id}>
                        <Checkbox checked={(newMarque.classes || []).includes(classe.id)} />
                        <ListItemText primary={`${classe.numero || "N/A"} - ${classe.intitule || "N/A"}`} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Aucune classe disponible</MenuItem>
                  )}
                  <MenuItem>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAddClassesSelectOpen(false);
                      }}
                      sx={{ mt: 1 }}
                    >
                      Fermer
                    </Button>
                  </MenuItem>
                </Select>
                {submitAttempted && !newMarque.classes.length && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                    Veuillez sélectionner au moins une classe.
                  </Typography>
                )}
              </FormControl>

              <FormControl
                fullWidth
                error={submitAttempted && !newMarque.zone_de_protection?.length}
              >
                <InputLabel id="zone-protection-label">Zones de Protection *</InputLabel>
                <Select
                  labelId="zone-protection-label"
                  multiple
                  value={newMarque.zone_de_protection || []}
                  onChange={(e) => setNewMarque({ ...newMarque, zone_de_protection: [...new Set(e.target.value)] })}
                  renderValue={(selected) =>
                    Array.isArray(selected) && selected.length
                      ? selected.join(", ")
                      : "Aucune zone sélectionnée"
                  }
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  {protectionZones.map((zone) => (
                    <MenuItem key={zone.id} value={zone.name}>
                      <Checkbox checked={(newMarque.zone_de_protection || []).includes(zone.name)} />
                      <ListItemText primary={zone.name} />
                    </MenuItem>
                  ))}
                </Select>
                {submitAttempted && !newMarque.zone_de_protection?.length && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                    Veuillez sélectionner au moins une zone de protection.
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Documents */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📄</span> Documents
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Certificat Dépôt PDF"
                type="file"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "application/pdf" }}
                onChange={(e) => setNewMarque({ ...newMarque, certificat_depot_pdf: e.target.files[0] })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Certificat Enregistrement PDF"
                type="file"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "application/pdf" }}
                onChange={(e) => setNewMarque({ ...newMarque, certificat_enregistrement_pdf: e.target.files[0] })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Dates */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📅</span> Dates Importantes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Date Dépôt"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newMarque.date_depot}
                onChange={(e) => {
                  const { date_de_protection, date_de_renouvellement } = calculateDerivedDates(e.target.value, newMarque.date_de_publication);
                  setNewMarque({
                    ...newMarque,
                    date_depot: e.target.value,
                    date_de_protection,
                    date_de_renouvellement,
                  });
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Date Publication"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newMarque.date_de_publication}
                onChange={(e) => {
                  const { date_de_fin_opposition } = calculateDerivedDates(newMarque.date_depot, e.target.value);
                  setNewMarque({
                    ...newMarque,
                    date_de_publication: e.target.value,
                    date_de_fin_opposition,
                  });
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Date Fin Opposition"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newMarque.date_de_fin_opposition}
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Date Protection"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newMarque.date_de_protection}
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Date Renouvellement"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newMarque.date_de_renouvellement}
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Informations Complémentaires */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>ℹ️</span> Informations Complémentaires
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                select
                label="État de la Marque"
                fullWidth
                value={newMarque.etat_de_la_marque || ""}
                onChange={(e) => setNewMarque({ ...newMarque, etat_de_la_marque: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              >
                {etatsDisponibles.map((etat) => (
                  <MenuItem key={etat} value={etat}>
                    {etat}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Noms Domaine Associés"
                fullWidth
                value={newMarque.noms_domaine_associes}
                onChange={(e) => setNewMarque({ ...newMarque, noms_domaine_associes: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Commentaire"
                fullWidth
                multiline
                rows={3}
                value={newMarque.commentaire}
                onChange={(e) => setNewMarque({ ...newMarque, commentaire: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <Divider sx={{ mt: 2 }} />

        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => {
              setOpenAdd(false);
              setNewMarque({
                numero: "",
                nom: "",
                proprietaire: "",
                descriptif: "",
                numero_marque: "",
                mandataire: "",
                surveillance_externe: false,
                cabinet_surveilleur: "",
                numero_dossier_surveilleur: "",
                classes: [],
                zone_de_protection: [],
                date_depot: "",
                date_de_publication: "",
                date_de_fin_opposition: "",
                date_de_protection: "",
                date_de_renouvellement: "",
                etat_de_la_marque: "",
                noms_domaine_associes: "",
                certificat_depot_pdf: null,
                certificat_enregistrement_pdf: null,
                commentaire: "",
                compte_comptable_id: "",
                compte_comptable_manuel: "",
              });
              setCustomCompteComptableAdd(false);
            }}
            sx={{
              color: theme.palette.error.main,
              borderRadius: "10px",
              px: 3,
              py: 1,
              fontWeight: 500,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleAddSubmit}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: "10px",
              px: 4,
              py: 1,
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={Slide}
        sx={{
          mt: 10,
          zIndex: 9999
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            minWidth: 320,
            fontWeight: 600,
            fontSize: "0.95rem",
            boxShadow: `0 4px 20px ${alpha(
              snackbarSeverity === "error"
                ? theme.palette.error.main
                : theme.palette.success.main,
              0.3
            )}`,
            borderRadius: "12px",
            py: 1.5,
            background: snackbarSeverity === "success"
              ? `linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)`
              : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>


      {/* Edit Dialog */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(
              theme.palette.background.paper,
              0.95
            )} 100%)`,
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <SectionTitle>Modifier Marque</SectionTitle>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Modifiez les informations de la marque
          </Typography>
        </DialogTitle>

        <Divider sx={{ mb: 2 }} />

        <DialogContent sx={{ pt: 2 }}>
          {/* Section: Informations Générales */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📋</span> Informations Générales
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              <TextField
                label="Numéro *"
                fullWidth
                name="Numéro"
                value={selectedMarque?.numero || ""}
                onChange={(e) => setSelectedMarque({ ...selectedMarque, numero: e.target.value })}
                error={submitAttemptedEdit && !selectedMarque?.numero?.trim()}
                helperText={
                  submitAttemptedEdit && !selectedMarque?.numero?.trim()
                    ? "Ce champ est requis"
                    : ""
                }
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />

              <FormControl
                fullWidth
                error={submitAttemptedEdit && !selectedMarque.societe_id}
              >
                <InputLabel id="societe-edit-label">Société *</InputLabel>
                <Select
                  labelId="societe-edit-label"
                  value={selectedMarque?.societe_id || ""}
                  onChange={(e) => {
                    const societeId = e.target.value;
                    setSelectedMarque({ ...selectedMarque, societe_id: societeId, compte_comptable_id: "" });
                    chargerComptesComptables(societeId, true);
                  }}
                  label="Société *"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': { borderRadius: "12px" }
                  }}
                >
                  <MenuItem value="" disabled>
                    Choisir une société
                  </MenuItem>
                  {societes.map((societe) => (
                    <MenuItem key={societe.id} value={societe.id}>
                      {societe.nom}
                    </MenuItem>
                  ))}
                </Select>
                {submitAttemptedEdit && !selectedMarque.societe_id && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                    Veuillez sélectionner une société.
                  </Typography>
                )}
              </FormControl>


              {!customCompteComptableEdit ? (
                <FormControl
                  fullWidth
                  disabled={!selectedMarque?.societe_id || loadingComptesEdit}
                  error={submitAttemptedEdit && !selectedMarque?.compte_comptable_id && !selectedMarque?.compte_comptable_manuel}
                >
                  <InputLabel id="compte-comptable-edit-label">Compte Comptable *</InputLabel>
                  <Select
                    labelId="compte-comptable-edit-label"
                    value={selectedMarque?.compte_comptable_id || ""}
                    onChange={(e) => setSelectedMarque({ ...selectedMarque, compte_comptable_id: e.target.value })}
                    label="Compte Comptable *"
                    sx={{
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      '& .MuiOutlinedInput-root': { borderRadius: "12px" }
                    }}
                  >
                    <MenuItem value="" disabled>
                      {loadingComptesEdit ? "Chargement..." : "Choisir un compte"}
                    </MenuItem>
                    {comptesComptablesEdit.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.code} — {c.libelle}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomCompteComptableEdit(true);
                        setSelectedMarque({ ...selectedMarque, compte_comptable_id: "" });
                      }}
                      sx={{
                        mx: 0.5, borderRadius: '8px',
                        color: '#2a5fa9', fontWeight: 600, fontSize: '0.875rem',
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&:hover': { backgroundColor: alpha('#2a5fa9', 0.08) },
                      }}
                    >
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #2a5fa9, #4cbed8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '1.1rem', fontWeight: 700,
                      }}>+</Box>
                      Saisir manuellement
                    </MenuItem>
                  </Select>
                  {submitAttemptedEdit && !selectedMarque?.compte_comptable_id && !selectedMarque?.compte_comptable_manuel && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                      Ce champ est requis.
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <TextField
                  label="Compte Comptable (saisie manuelle) *"
                  fullWidth
                  autoFocus
                  value={selectedMarque?.compte_comptable_manuel || ""}
                  onChange={(e) => setSelectedMarque({ ...selectedMarque, compte_comptable_manuel: e.target.value })}
                  error={submitAttemptedEdit && !selectedMarque?.compte_comptable_manuel}
                  helperText={submitAttemptedEdit && !selectedMarque?.compte_comptable_manuel ? "Ce champ est requis" : ""}
                  sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
                  InputProps={{
                    endAdornment: (
                      <Button size="small" onClick={() => { setCustomCompteComptableEdit(false); setSelectedMarque({ ...selectedMarque, compte_comptable_manuel: "" }); }}
                        sx={{ color: "error.main", minWidth: 'auto', fontSize: '0.75rem' }}>
                        ✕
                      </Button>
                    )
                  }}
                />
              )}
              <TextField
                label="Nom"
                fullWidth
                value={selectedMarque?.nom || ""}
                onChange={(e) => setSelectedMarque({ ...selectedMarque, nom: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Descriptif"
                fullWidth
                value={selectedMarque?.descriptif || ""}
                onChange={(e) => setSelectedMarque({ ...selectedMarque, descriptif: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Numéro Marque"
                fullWidth
                value={selectedMarque?.numero_marque || ""}
                onChange={(e) => setSelectedMarque({ ...selectedMarque, numero_marque: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Parties Prenantes */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>👥</span> Parties Prenantes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* PROPRIÉTAIRE EDIT */}
              {!customProprietaireEdit ? (
                <FormControl fullWidth>
                  <InputLabel id="proprietaire-edit-label">Propriétaire</InputLabel>
                  <Select
                    labelId="proprietaire-edit-label"
                    value={selectedMarque?.proprietaire || ""}
                    onChange={(e) => setSelectedMarque({ ...selectedMarque, proprietaire: e.target.value })}
                    label="Propriétaire"
                    sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "12px" }}
                  >
                    <MenuItem value=""><em>Aucun</em></MenuItem>
                    {uniqueProprietaires.map((proprietaire) => (
                      <MenuItem key={proprietaire} value={proprietaire}>{proprietaire}</MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={(e) => { e.stopPropagation(); setCustomProprietaireEdit(true); setSelectedMarque({ ...selectedMarque, proprietaire: "" }); }}
                      sx={{
                        mx: 0.5, borderRadius: '8px',
                        color: '#2a5fa9', fontWeight: 600, fontSize: '0.875rem',
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&:hover': { backgroundColor: alpha('#2a5fa9', 0.08) },
                      }}
                    >
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #2a5fa9, #4cbed8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '1.1rem', fontWeight: 700,
                      }}>+</Box>
                      Saisir manuellement
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Propriétaire"
                  fullWidth
                  autoFocus
                  value={selectedMarque?.proprietaire || ""}
                  onChange={(e) => setSelectedMarque({ ...selectedMarque, proprietaire: e.target.value })}
                  sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
                  InputProps={{
                    endAdornment: (
                      <Button size="small" onClick={() => { setCustomProprietaireEdit(false); setSelectedMarque({ ...selectedMarque, proprietaire: "" }); }}
                        sx={{ color: "error.main", minWidth: 'auto', fontSize: '0.75rem' }}>
                        ✕
                      </Button>
                    )
                  }}
                />
              )}

              {/* MANDATAIRE EDIT */}
              {!customMandataireEdit ? (
                <FormControl fullWidth>
                  <InputLabel id="mandataire-edit-label">Mandataire</InputLabel>
                  <Select
                    labelId="mandataire-edit-label"
                    value={selectedMarque?.mandataire || ""}
                    onChange={(e) => setSelectedMarque({ ...selectedMarque, mandataire: e.target.value })}
                    label="Mandataire"
                    sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "12px" }}
                  >
                    <MenuItem value=""><em>Aucun</em></MenuItem>
                    {uniqueMandataires.map((mandataire) => (
                      <MenuItem key={mandataire} value={mandataire}>{mandataire}</MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={(e) => { e.stopPropagation(); setCustomMandataireEdit(true); setSelectedMarque({ ...selectedMarque, mandataire: "" }); }}
                      sx={{
                        mx: 0.5, borderRadius: '8px',
                        color: '#2a5fa9', fontWeight: 600, fontSize: '0.875rem',
                        display: 'flex', alignItems: 'center', gap: 1,
                        '&:hover': { backgroundColor: alpha('#2a5fa9', 0.08) },
                      }}
                    >
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #2a5fa9, #4cbed8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '1.1rem', fontWeight: 700,
                      }}>+</Box>
                      Saisir manuellement
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Mandataire"
                  fullWidth
                  autoFocus
                  value={selectedMarque?.mandataire || ""}
                  onChange={(e) => setSelectedMarque({ ...selectedMarque, mandataire: e.target.value })}
                  sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
                  InputProps={{
                    endAdornment: (
                      <Button size="small" onClick={() => { setCustomMandataireEdit(false); setSelectedMarque({ ...selectedMarque, mandataire: "" }); }}
                        sx={{ color: "error.main", minWidth: 'auto', fontSize: '0.75rem' }}>
                        ✕
                      </Button>
                    )
                  }}
                />
              )}

            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Surveillance */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>🔍</span> Surveillance
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedMarque?.surveillance_externe || false}
                    onChange={(e) => setSelectedMarque({ ...selectedMarque, surveillance_externe: e.target.checked })}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    Surveillance Externe
                  </Typography>
                }
                sx={{
                  ml: 0,
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              />

              <FormControl fullWidth>
                <InputLabel id="cabinet-surveilleur-edit-label">Cabinet Surveilleur</InputLabel>
                <Select
                  labelId="cabinet-surveilleur-edit-label"
                  value={selectedMarque?.cabinet_surveilleur || ""}
                  onChange={(e) => setSelectedMarque({ ...selectedMarque, cabinet_surveilleur: e.target.value })}
                  label="Cabinet Surveilleur"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Aucun</em>
                  </MenuItem>
                  {uniqueCabinetsSurveilleurs.map((cabinet) => (
                    <MenuItem key={cabinet} value={cabinet}>
                      {cabinet}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Numéro Dossier Surveilleur"
                fullWidth
                value={selectedMarque?.numero_dossier_surveilleur || ""}
                onChange={(e) => setSelectedMarque({ ...selectedMarque, numero_dossier_surveilleur: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Classification */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>🏷️</span> Classification et Protection
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControl
                fullWidth
                error={submitAttemptedEdit && !selectedMarque?.classes?.length}
              >
                <InputLabel id="classes-edit-label">Classes *</InputLabel>
                <Select
                  labelId="classes-edit-label"
                  multiple
                  open={isClassesSelectOpen}
                  onOpen={() => setIsClassesSelectOpen(true)}
                  onClose={() => setIsClassesSelectOpen(false)}
                  value={selectedMarque?.classes || []}
                  onChange={(e) => {
                    const validValues = e.target.value.filter(val => val !== undefined && val !== null && val !== '');
                    setSelectedMarque({ ...selectedMarque, classes: [...new Set(validValues)] });
                  }}
                  renderValue={(selected) =>
                    selected.length
                      ? selected
                        .map((id) => {
                          const classe = classes.find((c) => c.id === id);
                          return classe ? `${classe.numero} - ${classe.intitule}` : null;
                        })
                        .filter(Boolean)
                        .join(", ")
                      : "Aucune classe sélectionnée"
                  }
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  {classes.length ? (
                    classes.map((classe) => (
                      <MenuItem key={classe.id} value={classe.id}>
                        <Checkbox checked={(selectedMarque?.classes || []).includes(classe.id)} />
                        <ListItemText primary={`${classe.numero || "N/A"} - ${classe.intitule || "N/A"}`} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Aucune classe disponible</MenuItem>
                  )}
                  <MenuItem>
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      onClick={() => setIsClassesSelectOpen(false)}
                      sx={{ mt: 1 }}
                    >
                      Fermer
                    </Button>
                  </MenuItem>
                </Select>
                {submitAttemptedEdit && !selectedMarque?.classes?.length && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                    Veuillez sélectionner au moins une classe.
                  </Typography>
                )}
              </FormControl>


              <FormControl
                fullWidth
                error={submitAttemptedEdit && !selectedMarque?.zone_de_protection?.length}
              >
                <InputLabel id="zone-protection-edit-label">Zones de Protection *</InputLabel>
                <Select
                  labelId="zone-protection-edit-label"
                  multiple
                  value={selectedMarque?.zone_de_protection || []}
                  onChange={(e) => setSelectedMarque({ ...selectedMarque, zone_de_protection: [...new Set(e.target.value)] })}
                  renderValue={(selected) =>
                    Array.isArray(selected) && selected.length
                      ? selected.join(", ")
                      : "Aucune zone sélectionnée"
                  }
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  {protectionZones.map((zone) => (
                    <MenuItem key={zone.id} value={zone.name}>
                      <Checkbox checked={(selectedMarque?.zone_de_protection || []).includes(zone.name)} />
                      <ListItemText primary={zone.name} />
                    </MenuItem>
                  ))}
                </Select>
                {submitAttemptedEdit && !selectedMarque?.zone_de_protection?.length && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                    Veuillez sélectionner au moins une zone de protection.
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Documents */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📄</span> Documents
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <TextField
                  label="Certificat Dépôt PDF"
                  type="file"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: "application/pdf" }}
                  onChange={(e) => setSelectedMarque({ ...selectedMarque, certificat_depot_pdf: e.target.files[0] })}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': {
                      borderRadius: "12px",
                    }
                  }}
                />
                {selectedMarque?.existing_certificat_depot_pdf && (
                  <Box display="flex" alignItems="center" sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: "8px",
                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }}>
                    <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }}>
                      📎 Fichier actuel : {selectedMarque.existing_certificat_depot_pdf.split("/").pop()}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Download />}
                      sx={{
                        ml: 2,
                        borderRadius: "8px",
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                      onClick={() =>
                        window.open(
                          `${process.env.REACT_APP_API_URL}/storage/certificats/${selectedMarque.existing_certificat_depot_pdf.split("/").pop()}`,
                          "_blank"
                        )
                      }
                    >
                      Télécharger
                    </Button>
                  </Box>
                )}
              </Box>

              <Box>
                <TextField
                  label="Certificat Enregistrement PDF"
                  type="file"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: "application/pdf" }}
                  onChange={(e) => setSelectedMarque({ ...selectedMarque, certificat_enregistrement_pdf: e.target.files[0] })}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': {
                      borderRadius: "12px",
                    }
                  }}
                />
                {selectedMarque?.existing_certificat_enregistrement_pdf && (
                  <Box display="flex" alignItems="center" sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: "8px",
                    backgroundColor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                  }}>
                    <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }}>
                      📎 Fichier actuel : {selectedMarque.existing_certificat_enregistrement_pdf.split("/").pop()}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Download />}
                      sx={{
                        ml: 2,
                        borderRadius: "8px",
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                      onClick={() =>
                        window.open(
                          `${process.env.REACT_APP_API_URL}/storage/certificats/${selectedMarque.existing_certificat_enregistrement_pdf.split("/").pop()}`,
                          "_blank"
                        )
                      }
                    >
                      Télécharger
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Dates */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📅</span> Dates Importantes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Date Dépôt"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedMarque?.date_depot || ""}
                onChange={(e) => {
                  const { date_de_protection, date_de_renouvellement } = calculateDerivedDates(
                    e.target.value,
                    selectedMarque.date_de_publication
                  );
                  setSelectedMarque({
                    ...selectedMarque,
                    date_depot: e.target.value,
                    date_de_protection,
                    date_de_renouvellement,
                  });
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Date Publication"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedMarque?.date_de_publication || ""}
                onChange={(e) => {
                  const { date_de_fin_opposition } = calculateDerivedDates(selectedMarque.date_depot, e.target.value);
                  setSelectedMarque({
                    ...selectedMarque,
                    date_de_publication: e.target.value,
                    date_de_fin_opposition,
                  });
                }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Date Fin Opposition"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedMarque?.date_de_fin_opposition || ""}
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Date Protection"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedMarque?.date_de_protection || ""}
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Date Renouvellement"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedMarque?.date_de_renouvellement || ""}
                InputProps={{ readOnly: true }}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Informations Complémentaires */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>ℹ️</span> Informations Complémentaires
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                select
                label="État de la Marque"
                fullWidth
                value={selectedMarque?.etat_de_la_marque || ""}
                onChange={(e) => setSelectedMarque({ ...selectedMarque, etat_de_la_marque: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              >
                {etatsDisponibles.map((etat) => (
                  <MenuItem key={etat} value={etat}>
                    {etat}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Noms Domaine Associés"
                fullWidth
                value={selectedMarque?.noms_domaine_associes || ""}
                onChange={(e) => setSelectedMarque({ ...selectedMarque, noms_domaine_associes: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
              <TextField
                label="Commentaire"
                fullWidth
                multiline
                rows={3}
                value={selectedMarque?.commentaire || ""}
                onChange={(e) => setSelectedMarque({ ...selectedMarque, commentaire: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <Divider sx={{ mt: 2 }} />

        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => {
              setOpenEdit(false);
              setSubmitAttemptedEdit(false);
              setCustomProprietaireEdit(false);
              setCustomMandataireEdit(false);
              setCustomCompteComptableEdit(false);
            }} sx={{
              color: theme.palette.error.main,
              borderRadius: "10px",
              px: 3,
              py: 1,
              fontWeight: 500,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: "10px",
              px: 4,
              py: 1,
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            minWidth: 450,
            px: 2,
            py: 1,
          },
        }}
      >
        <DialogTitle>
          <SectionTitle sx={{ justifyContent: 'center', textAlign: 'center' }}>Supprimer Marque</SectionTitle>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', textAlign: 'center', mt: 1, fontSize: '0.95rem' }}>
            Voulez-vous vraiment supprimer cette marque ?
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', textAlign: 'center', mt: 1, fontSize: '0.95rem' }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDelete(false)}
            sx={{
              color: theme.palette.error.main,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "8px",
              background: alpha(theme.palette.error.main, 0.8),
              "&:hover": {
                background: theme.palette.error.main,
                transform: "scale(1.05)",
              },
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Marques;